import type { Edge, Node } from "@xyflow/react"

export type WorkflowIssueSeverity = "error" | "warning"

export type WorkflowIssue = {
  code: string
  /** English fallback text; render via t(messageKey, { defaultValue: message, ...messageParams }). */
  message: string
  /** i18n key under workflowLanding.validation.issues.* */
  messageKey: string
  messageParams?: Record<string, string | number>
  severity: WorkflowIssueSeverity
  nodeId?: string
}

const ISSUE_KEY_PREFIX = "workflowLanding.validation.issues."

function issueKey(suffix: string): string {
  return ISSUE_KEY_PREFIX + suffix
}

function edgeMissingEndpointKey(hasSource: boolean, hasTarget: boolean): string {
  if (!hasSource && !hasTarget) return "edge_missing_both"
  return hasSource ? "edge_missing_target" : "edge_missing_source"
}

function buildAdjacency(edges: Edge[]): Map<string, string[]> {
  const adjacency = new Map<string, string[]>()
  for (const edge of edges) {
    const source = edge.source
    const target = edge.target
    if (!adjacency.has(source)) {
      adjacency.set(source, [])
    }
    adjacency.get(source)?.push(target)
  }
  return adjacency
}

function buildReverseAdjacency(edges: Edge[]): Map<string, string[]> {
  const adjacency = new Map<string, string[]>()
  for (const edge of edges) {
    const source = edge.source
    const target = edge.target
    if (!adjacency.has(target)) {
      adjacency.set(target, [])
    }
    adjacency.get(target)?.push(source)
  }
  return adjacency
}

function collectReachable(startNodeIds: string[], adjacency: Map<string, string[]>): Set<string> {
  const visited = new Set<string>()
  const queue = [...startNodeIds]

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue
    if (visited.has(current)) continue
    visited.add(current)

    const outgoers = adjacency.get(current) ?? []
    for (const next of outgoers) {
      if (!visited.has(next)) {
        queue.push(next)
      }
    }
  }

  return visited
}

function findDirectedCycleNodeId(nodeIds: Set<string>, edges: Edge[]): string | undefined {
  const adjacency = buildAdjacency(edges)
  const state = new Map<string, "visiting" | "done">()

  for (const startNodeId of nodeIds) {
    if (state.has(startNodeId)) continue
    state.set(startNodeId, "visiting")
    const stack = [{ nodeId: startNodeId, nextIndex: 0 }]

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]
      const neighbors = adjacency.get(frame.nodeId) ?? []
      if (frame.nextIndex >= neighbors.length) {
        state.set(frame.nodeId, "done")
        stack.pop()
        continue
      }

      const nextNodeId = neighbors[frame.nextIndex]
      frame.nextIndex += 1
      if (state.get(nextNodeId) === "visiting") {
        return nextNodeId
      }
      if (!state.has(nextNodeId)) {
        state.set(nextNodeId, "visiting")
        stack.push({ nodeId: nextNodeId, nextIndex: 0 })
      }
    }
  }

  return undefined
}

function isReachable(startNodeId: string, targetNodeIds: Set<string>, edges: Edge[]): boolean {
  const adjacency = buildAdjacency(edges)
  const visited = new Set<string>()
  const queue: string[] = [startNodeId]

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue
    if (visited.has(current)) continue
    visited.add(current)

    if (targetNodeIds.has(current)) {
      return true
    }

    const outgoers = adjacency.get(current) ?? []
    for (const next of outgoers) {
      if (!visited.has(next)) {
        queue.push(next)
      }
    }
  }

  return false
}

export function getWorkflowIssues(nodes: Node[], edges: Edge[]): WorkflowIssue[] {
  const issues: WorkflowIssue[] = []
  const nodesById = new Map(nodes.map((node) => [node.id, node]))

  const startNodes = nodes.filter((n) => n.type === "start")
  if (startNodes.length === 0) {
    issues.push({
      code: "missing_start",
      messageKey: issueKey("missing_start"),
      severity: "error",
      message: "Workflow must have exactly one Start node (currently none).",
    })
  } else if (startNodes.length > 1) {
    issues.push({
      code: "multiple_start",
      messageKey: issueKey("multiple_start"),
      severity: "error",
      message: "Workflow must have exactly one Start node (currently multiple).",
    })
  }

  const endNodes = nodes.filter((n) => n.type === "end")
  if (endNodes.length < 1) {
    issues.push({
      code: "missing_end",
      messageKey: issueKey("missing_end"),
      severity: "error",
      message: "Workflow must have at least one End node.",
    })
  }

  if (nodes.length > 1 && edges.length === 0) {
    issues.push({
      code: "disconnected_graph",
      messageKey: issueKey("disconnected_graph"),
      severity: "error",
      message: "Workflow nodes must be connected (no edges found).",
    })
  }

  if (startNodes.length === 1 && endNodes.length > 0) {
    const endNodeIds = new Set(endNodes.map((n) => n.id))
    const reachable = isReachable(startNodes[0].id, endNodeIds, edges)
    if (!reachable) {
      issues.push({
        code: "end_not_reachable",
        messageKey: issueKey("end_not_reachable"),
        severity: "error",
        message: "At least one End node must be reachable from the Start node.",
      })
    }
  }

  const loopContainerIds = new Set(
    nodes.filter((node) => node.type === "loopContainer").map((node) => node.id)
  )
  const topLevelNodeIds = new Set(
    nodes
      .filter((node) => !node.parentId || !loopContainerIds.has(node.parentId))
      .map((node) => node.id)
  )
  const topLevelEdges = edges.filter(
    (edge) => topLevelNodeIds.has(edge.source) && topLevelNodeIds.has(edge.target)
  )

  if (startNodes.length === 1 && endNodes.length > 0) {
    const startNodeId = startNodes[0].id
    const endNodeIds = endNodes.map((node) => node.id)
    const reachableFromStart = collectReachable([startNodeId], buildAdjacency(topLevelEdges))
    const canReachEnd = collectReachable(endNodeIds, buildReverseAdjacency(topLevelEdges))
    const hasEndReachable = endNodeIds.some((endNodeId) => reachableFromStart.has(endNodeId))

    if (hasEndReachable) {
      for (const nodeId of reachableFromStart) {
        if (canReachEnd.has(nodeId)) continue
        issues.push({
          code: "branch_missing_end",
          messageKey: issueKey("branch_missing_end"),
          severity: "error",
          nodeId,
          message:
            "This node is on a branch that never reaches an End node. Reroute edges or add a merge so every branch reaches End.",
        })
      }
    }

    // #2965: a node nothing feeds has no trigger and never runs — typically a
    // data source dragged in as a side input to an LLM node. The backend now
    // starts a parentless Attachment node that feeds something from Start on
    // its own, so that shape is fine; every other unreachable node is warned
    // about so the graph reads the way it runs.
    const outgoing = buildAdjacency(topLevelEdges)
    const hasIncoming = new Set(topLevelEdges.map((edge) => edge.target))
    for (const nodeId of topLevelNodeIds) {
      if (reachableFromStart.has(nodeId)) continue
      const unreachable = nodesById.get(nodeId)
      if (!unreachable || unreachable.type === "start") continue
      // Exactly the backend's condition (graph_builder.py): it auto-starts an
      // attachment that feeds something AND has no incoming edge. An
      // attachment fed by an unreachable node is not auto-started, so it stays
      // a real warning rather than a silent no-op.
      if (
        unreachable.type === "attachment" &&
        (outgoing.get(nodeId)?.length ?? 0) > 0 &&
        !hasIncoming.has(nodeId)
      ) {
        continue
      }
      issues.push({
        code: "node_unreachable",
        messageKey: issueKey("node_unreachable"),
        severity: "warning",
        nodeId,
        message:
          "This node is never reached from Start, so it will not run. Connect Start (or a node that runs) to it.",
      })
    }
  }

  const cycleNodeId = findDirectedCycleNodeId(topLevelNodeIds, topLevelEdges)
  if (cycleNodeId) {
    issues.push({
      code: "cycle_detected",
      messageKey: issueKey("cycle_detected"),
      severity: "error",
      nodeId: cycleNodeId,
      message:
        "Workflow contains a cycle. Use a Loop node for intentional repetition or remove the cyclic edge.",
    })
  }

  const parallelNodes = nodes.filter((n) => n.type === "parallel")
  const mergeNodes = nodes.filter((n) => n.type === "merge")
  if (parallelNodes.length > mergeNodes.length) {
    issues.push({
      code: "parallel_missing_merge",
      messageKey: issueKey("parallel_missing_merge"),
      messageParams: { parallelCount: parallelNodes.length, mergeCount: mergeNodes.length },
      severity: "error",
      message: `Workflow has ${parallelNodes.length} parallel node(s) but only ${mergeNodes.length} merge node(s).`,
    })
  }

  for (const edge of edges) {
    const sourceNode = nodesById.get(edge.source)
    const targetNode = nodesById.get(edge.target)

    if (!sourceNode || !targetNode) {
      const missingParts = [
        ...(sourceNode ? [] : [`source "${edge.source}"`]),
        ...(targetNode ? [] : [`target "${edge.target}"`]),
      ]
      issues.push({
        code: "edge_missing_endpoint",
        messageKey: issueKey(edgeMissingEndpointKey(Boolean(sourceNode), Boolean(targetNode))),
        messageParams: { edgeId: edge.id, sourceId: edge.source, targetId: edge.target },
        severity: "error",
        message: `Edge "${edge.id}" references missing ${missingParts.join(" and ")}.`,
      })
      continue
    }

    if (targetNode.type === "start") {
      issues.push({
        code: "start_has_input",
        messageKey: issueKey("start_has_input"),
        severity: "error",
        nodeId: targetNode.id,
        message: "Start node cannot have incoming edges.",
      })
    }

    if (sourceNode.type === "end") {
      issues.push({
        code: "end_has_output",
        messageKey: issueKey("end_has_output"),
        severity: "error",
        nodeId: sourceNode.id,
        message: "End node cannot have outgoing edges.",
      })
    }

    const sourceParentId = sourceNode.parentId
    const targetParentId = targetNode.parentId
    if ((sourceParentId || targetParentId) && sourceParentId !== targetParentId) {
      const loopId =
        (typeof sourceParentId === "string" && sourceParentId) ||
        (typeof targetParentId === "string" && targetParentId) ||
        undefined
      issues.push({
        code: "loop_child_external_connection",
        messageKey: issueKey("loop_child_external_connection"),
        severity: "error",
        nodeId: loopId,
        message: "Edges cannot connect directly to nodes inside a loop from outside the loop.",
      })
    }

    const sourceHandle = edge.sourceHandle
    const targetHandle = edge.targetHandle

    if (sourceNode.type === "conditional") {
      if (sourceHandle !== "true" && sourceHandle !== "false") {
        issues.push({
          code: "conditional_missing_source_handle",
          messageKey: issueKey("conditional_missing_source_handle"),
          severity: "error",
          nodeId: sourceNode.id,
          message: "Conditional node edges must use sourceHandle 'true' or 'false'.",
        })
      }
    } else if (typeof sourceHandle === "string") {
      issues.push({
        code: "invalid_source_handle",
        messageKey: issueKey("invalid_source_handle"),
        severity: "error",
        nodeId: sourceNode.id,
        message: "sourceHandle is only allowed on conditional nodes.",
      })
    }

    if (targetNode.type === "conditional") {
      if (targetHandle !== "input") {
        issues.push({
          code: "conditional_missing_target_handle",
          messageKey: issueKey("conditional_missing_target_handle"),
          severity: "error",
          nodeId: targetNode.id,
          message: "Conditional node edges must use targetHandle 'input'.",
        })
      }
    } else if (typeof targetHandle === "string") {
      issues.push({
        code: "invalid_target_handle",
        messageKey: issueKey("invalid_target_handle"),
        severity: "error",
        nodeId: targetNode.id,
        message: "targetHandle is only allowed on conditional nodes.",
      })
    }
  }

  const hasProcessorNode = nodes.some((n) => n.type === "llm" || n.type === "agent")
  if (!hasProcessorNode) {
    issues.push({
      code: "missing_processor",
      messageKey: issueKey("missing_processor"),
      severity: "warning",
      message:
        "This workflow has no LLM or Agent node. Without a processor node, it will not produce any output.",
    })
  }

  for (const node of nodes) {
    if (node.type === "tool") {
      const toolName = (node.data as Record<string, unknown> | undefined)?.toolName
      if (typeof toolName !== "string" || !toolName) {
        issues.push({
          code: "tool_missing_toolName",
          messageKey: issueKey("tool_missing_toolName"),
          severity: "error",
          nodeId: node.id,
          message: "Tool node is missing data.toolName.",
        })
      }
    }

    if (node.type === "http") {
      const url = (node.data as Record<string, unknown> | undefined)?.url
      if (typeof url !== "string" || url.trim().length === 0) {
        issues.push({
          code: "http_missing_url",
          messageKey: issueKey("http_missing_url"),
          severity: "error",
          nodeId: node.id,
          message: "HTTP node is missing a URL (data.url).",
        })
      }
    }
  }

  return issues
}
